'use client';

import * as React from 'react';
import { getCoverImageTheme } from 'app/blog/[domain]/_components/cover-image-theme';
import type { PostCardFields } from 'app/blog/[domain]/(standalone)/_server/types';
import { FormattedDate } from 'components/formatted-date';
import { IconArrowNarrowRight } from 'icon/Arrows/arrow-narrow-right';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { defaultAuthorAtom } from '../../_components/design-state';
import { PostLink } from './post-link';

export interface PostCardProps {
  post: PostCardFields;
  className?: string;
  'aria-label'?: string;
}

export function PostCard({
  post,
  className,
  ...props
}: PostCardProps): JSX.Element {
  const t = useTranslations('Blog.PostCard');
  const defaultAuthor = useAtomValue(defaultAuthorAtom);
  return (
    <PostLink
      href={`/post/${post.slug}_${post.id}`}
      className={['post-card', className].filter(Boolean).join(' ')}
      aria-label={props['aria-label']}
    >
      <div className="post-card-media">
        <div
          className="post-card-image"
          data-theme-cover={getCoverImageTheme(post.coverImage)}
          style={{
            backgroundImage: `url(${post.coverImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: '50% 50%',
          }}
        />
        <div className="post-card-overlay">
          <div className="post-card-author">
            <p className="post-card-author-name">{defaultAuthor.name}</p>
            <p className="post-card-overlay-date">
              <FormattedDate date={post.publishedAt} />
            </p>
          </div>
          {post.tags[0] && <p className="post-card-tag">{post.tags[0]}</p>}
        </div>
      </div>
      <h3
        className="post-card-title"
        dangerouslySetInnerHTML={{ __html: post.titleHtml }}
      />
      <time
        className="post-card-date"
        dateTime={new Date(post.publishedAt).toISOString()}
      >
        <FormattedDate date={post.publishedAt} />
      </time>
      <p className="post-card-excerpt">{post.excerpt}</p>
      <p className="post-card-action">
        {t('ReadPost')}
        <IconArrowNarrowRight className="post-card-action-icon" />
      </p>
    </PostLink>
  );
}
